// Single source of truth for feature/scope colors — used by Updates tree connector lines,
// scope pills, and any feature-branded UI. Aligns with STYLING_SYSTEM.md Signal Palette.

export type ScopeKey =
  | "Leads"
  | "Settings"
  | "Billing"
  | "Topica"
  | "Dashboard"
  | "Platform"
  | "Operations"
  | "Safety"
  | "Supply"
  | "Staff"
  | "Client"
  | "Other"

// Quick-pick presets shown in ScopePicker — operations vocabulary only.
// Internal product scopes (Topica, Dashboard, Billing, etc.) are kept in
// ScopeKey for backwards compatibility but excluded from the picker.
export const SCOPES: ScopeKey[] = [
  "Platform",
  "Operations",
  "Safety",
  "Supply",
  "Staff",
  "Client",
  "Other",
]

// ---------------------------------------------------------------------------
// glitchOS color palette — 8 signal colors used across scope pickers,
// connector lines, and any scope-branded surface.
// ---------------------------------------------------------------------------

export type GlitchColor = "teal" | "violet" | "amber" | "rose" | "luster" | "emerald" | "sky" | "orange" | "white"

export type GlitchPaletteEntry = {
  name: GlitchColor
  hex: string
  label: string
  pillCls: string
  lineOpacity: number
}

export const GLITCH_PALETTE: GlitchPaletteEntry[] = [
  { name: "teal",    hex: "#2dd4bf", label: "Teal",    pillCls: "text-teal-400 bg-teal-500/10 border-teal-400/20",       lineOpacity: 0.55 },
  { name: "violet",  hex: "#a78bfa", label: "Violet",  pillCls: "text-violet-400 bg-violet-500/10 border-violet-400/20", lineOpacity: 0.55 },
  { name: "amber",   hex: "#fbbf24", label: "Amber",   pillCls: "text-amber-400 bg-amber-500/10 border-amber-400/20",    lineOpacity: 0.55 },
  { name: "rose",    hex: "#fb7185", label: "Rose",    pillCls: "text-rose-400 bg-rose-500/10 border-rose-400/20",       lineOpacity: 0.55 },
  { name: "luster",  hex: "#6675c8", label: "Luster",  pillCls: "text-luster-400 bg-luster-500/10 border-luster-400/20",lineOpacity: 0.55 },
  { name: "emerald", hex: "#6ee7b7", label: "Emerald", pillCls: "text-emerald-400 bg-emerald-500/10 border-emerald-400/20",lineOpacity: 0.55 },
  { name: "sky",     hex: "#38bdf8", label: "Sky",     pillCls: "text-sky-400 bg-sky-500/10 border-sky-400/20",          lineOpacity: 0.55 },
  { name: "orange",  hex: "#fb923c", label: "Orange",  pillCls: "text-orange-400 bg-orange-500/10 border-orange-400/20", lineOpacity: 0.55 },
  { name: "white",   hex: "#ffffff", label: "Neutral", pillCls: "text-white/40 bg-white/[0.04] border-white/[0.06]",     lineOpacity: 0.15 },
]

// Scope → glitchOS color assignment for predefined scopes
const SCOPE_COLOR_MAP: Record<ScopeKey, GlitchColor> = {
  Platform:   "teal",
  Operations: "emerald",
  Safety:     "amber",
  Supply:     "sky",
  Staff:      "violet",
  Client:     "rose",
  Leads:      "violet",
  Billing:    "amber",
  Dashboard:  "luster",
  Topica:     "rose",
  Settings:   "white",
  Other:      "white",
}

/** Hex color for SVG/inline styles (connector lines, etc.). Use with opacity in CSS. */
export const SCOPE_LINE_HEX: Record<ScopeKey, string> = Object.fromEntries(
  SCOPES.map((s) => [s, GLITCH_PALETTE.find((p) => p.name === SCOPE_COLOR_MAP[s])?.hex ?? "#ffffff"])
) as Record<ScopeKey, string>

/** Tailwind classes for pills/tags (text, bg, border). */
export const SCOPE_PILL_CLASSES: Record<ScopeKey, string> = Object.fromEntries(
  SCOPES.map((s) => [s, GLITCH_PALETTE.find((p) => p.name === SCOPE_COLOR_MAP[s])?.pillCls ?? "text-white/40 bg-white/[0.04] border-white/[0.06]"])
) as Record<ScopeKey, string>

/**
 * Hash any scope string to a deterministic glitchOS palette entry.
 * Used for custom scopes not in the predefined list.
 */
export function getScopePaletteEntry(scope: string): GlitchPaletteEntry {
  const known = SCOPE_COLOR_MAP[scope as ScopeKey]
  if (known) return GLITCH_PALETTE.find((p) => p.name === known) ?? GLITCH_PALETTE[8]
  // Hash unknown strings to a palette color (excluding white/neutral)
  let h = 0
  for (let i = 0; i < scope.length; i++) h = (h * 31 + scope.charCodeAt(i)) & 0xfffff
  return GLITCH_PALETTE[h % 8]
}

/** Line color as rgba string for connector lines (scope color at readable opacity). */
export function getScopeLineColor(scope: string | null): string {
  if (!scope) return "rgba(255,255,255,0.15)"
  const entry = getScopePaletteEntry(scope)
  const hex = entry.hex
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r},${g},${b},${entry.lineOpacity})`
}

/**
 * Connector line color: use user-chosen scope_color when set, otherwise derive from scope name.
 * Accepts scope_color from DB (string | null) for backward compat with entries that have no scope_color.
 */
export function getLineColorFromExplicit(
  scopeColor: string | null,
  scope: string | null
): string {
  if (scopeColor) {
    const entry = GLITCH_PALETTE.find((p) => p.name === scopeColor)
    if (entry) {
      const { hex, lineOpacity } = entry
      const r = parseInt(hex.slice(1, 3), 16)
      const g = parseInt(hex.slice(3, 5), 16)
      const b = parseInt(hex.slice(5, 7), 16)
      return `rgba(${r},${g},${b},${lineOpacity})`
    }
  }
  return getScopeLineColor(scope)
}

export function getScopePillClasses(scope: string | null): string {
  if (!scope) return SCOPE_PILL_CLASSES.Other
  return getScopePaletteEntry(scope).pillCls
}
